import { Alert, Grid, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import { useCallback, useMemo, useState } from 'react';

import { DescriptionList } from '../../../../../../design-system/body-sections/description-list/DescriptionList';
import { DescriptionListItem } from '../../../../../../design-system/body-sections/description-list/DescriptionListItem';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type { UpdateInvestorFirmInput } from '../../../../../../graphql';
import { SECTIONS_SIZING } from '../../../crm-sales-coverage/employee-detail/models/UserDetailsSection.model';
import type { InvestorFirmFragment } from '../../graphql/__generated__';
import { useUpdateInvestorFirm } from '../hooks/useUpdateInvestorFirm';
import {
  getInvestorFirmDetailInitialValues,
  INVESTOR_FIRM_DETAIL_FIELD_LABELS,
  investorFirmValidationSchema,
} from '../models/InvestorFirmDetail.model';
import { FirmDetailsFields } from './FirmDetailsFields';

export type FirmDetailsSectionProps = Readonly<{
  tenantId?: string;
  firm: InvestorFirmFragment;
}>;

export function FirmDetailsSection({ tenantId, firm }: FirmDetailsSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const initialValues = useMemo(() => getInvestorFirmDetailInitialValues(firm), [firm]);
  const { updateInvestorFirm, error } = useUpdateInvestorFirm(tenantId);

  const handleSubmit = useCallback(
    async (values: UpdateInvestorFirmInput) => {
      try {
        const firmDetails = pick(values, [
          'name',
          'investorType',
          'investorSubType',
          'cik1',
          'cik2',
          'crd',
          'lei',
          'factSet',
          'description',
        ]);

        const firmKeyOrId = firm.firmKey ?? firm.id;

        if (isNil(firmKeyOrId)) {
          throw new Error('Firm Key or ID is required');
        }

        await updateInvestorFirm(firmKeyOrId, {
          ...firmDetails,
          website1: firm.website1,
          website2: firm.website2,
          address1: firm.address1,
          address2: firm.address2,
          phoneNumber1: firm.phone1,
          phoneNumber2: firm.phone2,
          email1: firm.emailAddress1,
          email2: firm.emailAddress2,
          faxNumber1: firm.fax1,
          faxNumber2: firm.fax2,
          mpid: firm.mpid,
          status: firm.status,
        });
        setIsEditing(false);
        SnackbarManager.success('Firm details updated successfully.');
      } catch {
        SnackbarManager.error('An error occurred while updating firm details.');
      }
    },
    [updateInvestorFirm, firm]
  );
  const formik = useFormik<UpdateInvestorFirmInput>({
    initialValues,
    validationSchema: investorFirmValidationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    formik.resetForm();
  }, [formik]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <FormikProvider value={formik}>
      <Grid item {...SECTIONS_SIZING}>
        <Section
          title="Firm Details"
          data-testid="firm-details-section"
          actions={[
            <PageGridSectionActions
              key="page-grid-section-actions"
              isSubmitting={formik.isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={handleCancel}
              onSave={handleSave}
              canEdit
            />,
          ]}
        >
          {error && <Alert severity="error">{error.message}</Alert>}
          {isEditing ? (
            <FirmDetailsFields disableFirmKey />
          ) : (
            <DescriptionList>
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.firmKey}
                value={firm.firmKey}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.name}
                value={defaultTo(firm.name, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.investorType}
                value={defaultTo(firm.firmType, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.investorSubType}
                value={defaultTo(firm.firmSubType, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.cik1}
                value={defaultTo(firm.cik1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.cik2}
                value={defaultTo(firm.cik2, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.crd}
                value={defaultTo(firm.crd, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.lei}
                value={defaultTo(firm.lei, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.factSet}
                value={defaultTo(firm.factSet, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.description}
                value={defaultTo(firm.description, '-')}
              />
            </DescriptionList>
          )}
        </Section>
      </Grid>
    </FormikProvider>
  );
}
